import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { useLocation } from 'wouter'

export default function useCategories(layout: GraphQL.LayoutFragmentFragment) {
  const [, navigate] = useLocation()
  const [updatePositions] = useMutation(AdminAPI.category.BULK_UPDATE_POSITIONS)
  const [deleteCategory] = useMutation(AdminAPI.category.DELETE)

  const { data, loading } = useQuery(AdminAPI.category.GET_BY_LAYOUT, {
    variables: { layoutId: layout.id }
  })

  const categories = useMemo(() => {
    return (
      data?.getCategoriesByLayout
        .map(function (fragment) {
          return getFragment(SharedAPI.category.CATEGORY_FRAGMENT, fragment)
        })
        .toSorted((catA, catB) => catA.position - catB.position) ?? []
    )
  }, [data])

  const moveCategory = useCallback(
    (slug: string, position: number) => {
      if (position < 0 || position >= categories.length) return

      const category = categories.find(c => c.slug === slug)

      if (!category) return

      const catPositions = categories
        .toSpliced(category?.position, 1)
        .toSpliced(position, 0, category)
        .map(function (category, index) {
          return {
            ...category,
            cover: getFragment(SharedAPI.file.FILE_FRAGMENT, category.cover),
            __typename: 'Category',
            position: index
          } as GraphQL.Category
        })

      const input = catPositions.map(({ slug, position }) => ({
        slug,
        position
      }))

      updatePositions({
        variables: { input, layoutId: layout.id },
        optimisticResponse: {
          bulkUpdateCategoryPositions: catPositions
        }
      })
    },
    [categories, layout.id, updatePositions]
  )

  const editCategory = useCallback(
    (id: number) => {
      const slug = categories.find(c => c.id === id)?.slug

      navigate(`/category/${layout.version}/${slug}`)
    },
    [categories, navigate, layout.version]
  )

  const removeCategory = useCallback(
    (id: number) => {
      deleteCategory({
        variables: { id },
        refetchQueries: [AdminAPI.category.GET_BY_LAYOUT]
      })
    },
    [deleteCategory]
  )

  return {
    categories,
    loading,
    editCategory,
    removeCategory,
    moveCategory
  }
}
